import React, { ReactNode, createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, LocationOn, Work, Person, Create } from "@mui/icons-material";
import {
  newUser,
  ContactDetail,
  AddressDetail,
  PersonalData,
  ProfessionalData,
  Disability,
  RoleType,
  ValidadeUser,
} from "../types/Models";
import { StepType } from "../types/Page";
import { formatarDatetimeToDataDb } from "../utils/Masks";
import axios from "axios";
import { updateUserComplete, validateExistsUsers } from "../API/fetchUsuarios";

export const CADASTRO_USER_UPDATED_EVENT = "cadastro:user-updated";

export type CadastroPayload = {
  userData: {
    nome: string;
    email: string;
    provedor: string;
    tipo_usuario: string;
    CPF: string;
    senha: string;
  };
  personalData: Record<string, any>;
  addressData: AddressDetail;
  contactData: ContactDetail;
  professionalData: Record<string, any>;
  deficiencyData: { deficiencia: Disability[] } | null;
  captcha_token?: string;
};

interface CadastroContextType {
  loadingRegister: boolean;
  setLoadingRegister: React.Dispatch<React.SetStateAction<boolean>>;
  loadingUpdate: boolean;
  setLoadingUpdate: React.Dispatch<React.SetStateAction<boolean>>;
  loadingValidateExistsUser: boolean;
  userData: newUser;
  setUserData: React.Dispatch<React.SetStateAction<newUser>>;
  personalData: PersonalData;
  setPersonalData: React.Dispatch<React.SetStateAction<PersonalData>>;
  addressData: AddressDetail;
  setAddressData: React.Dispatch<React.SetStateAction<AddressDetail>>;
  contactData: ContactDetail;
  setContactData: React.Dispatch<React.SetStateAction<ContactDetail>>;
  professionalData: ProfessionalData;
  setProfessionalData: React.Dispatch<React.SetStateAction<ProfessionalData>>;
  disabilities: Disability[];
  setDisabilities: React.Dispatch<React.SetStateAction<Disability[]>>;
  areasActivity: number[],
  setAreasActivity: React.Dispatch<React.SetStateAction<number[]>>;
  steps: StepType[];
  activeStepIndex: number;
  setActiveStepIndex: React.Dispatch<React.SetStateAction<number>>;
  stepCompleted: boolean[];
  setStepCompletedAtIndex: (index: number, completed: boolean) => void;
  captchaToken: string | null;
  setCaptchaToken: React.Dispatch<React.SetStateAction<string | null>>;
  registrationCompleted: boolean;
  setRegistrationCompleted: React.Dispatch<React.SetStateAction<boolean>>;
  handleNextStep: () => void;
  handlePrevStep: () => void;
  handleNavigate: (userType: string) => void;
  handleUpdateUser: (step: number) => void;
  validadeExistsUser: () => any;
}

const CadastroContext = createContext<CadastroContextType | undefined>(
  undefined,
);

interface CadastroProviderProps {
  children: ReactNode;
  basePath?: string | null;
  onRegisterSubmit?: (payload: CadastroPayload) => Promise<void>;
  onRegisterSuccess?: () => void;
  onBackStart?: () => void;
  initialCpf?: string;
}

export const CadastroProvider: React.FC<CadastroProviderProps> = ({
  children,
  basePath: _basePath = "/cadastro",
  onRegisterSubmit,
  onRegisterSuccess,
  onBackStart,
  initialCpf = "",
}) => {
  const navigate = useNavigate();
  //const location = useLocation();

  const initialUserType = RoleType.USUARIO;

  const [loadingRegister, setLoadingRegister] = useState<boolean>(false);
  const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
  const [loadingValidateExistsUser, setLoadingValidateExistsUser] = useState<boolean>(false);

  const [userData, setUserData] = useState<newUser>({
    nome: "",
    email: "",
    provedor: "",
    confirmarEmail: "",
    confirmarProvedor: "",
    tipo: initialUserType,
    cpf: initialCpf,
    senha: "",
    confirmarSenha: "",
    force_password_change: false,
  });

  const [personalData, setPersonalData] = useState<PersonalData>({
    rg: "",
    emissor: "",
    uf: "",
    identidade_genero: undefined,
    identidade_genero_outros: "",
    cor_raca: "",
    data_nascimento: undefined,
    escolaridade: undefined,
    profissao: "",
    profissao_outros: "",
    possui_nome_social: false,
    possui_deficiencia: false,
    deficiencia_outros: "",
    nome_social: "",
    estrutura_participacao: "",
  });

  const [addressData, setAddressData] = useState<AddressDetail>({
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    uf: "",
  });

  const [contactData, setContactData] = useState<ContactDetail>({
    telefone_residencial: "",
    telefone_celular: "",
    possui_whatsapp: false,
    telefone_institucional: "",
    email_institucional: "",
  });

  const [professionalData, setProfessionalData] = useState<ProfessionalData>({
    ambito_id: "",
    municipio_id: "",
    lotacao_id: "",
    vinculo_id: "",
    funcao_id: "",
    superintendencia_id: "",
    unidade_id: "",
    diretoria_regional_id: "",
    diretoria_interna_id: "",
    instituicao_id: "",
    orgao_atuacao_id: "",
  });

  // Lista de áreas de atuação que vão para multselect
  const [areasActivity, setAreasActivity] = useState<number[]>([]);

  // Lista de deficiências que vão para o multSelect
  const [disabilities, setDisabilities] = useState<Disability[]>([]);

  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const [stepCompleted, setStepCompleted] = useState<boolean[]>(
    new Array(5).fill(false),
  );
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [registrationCompleted, setRegistrationCompleted] = useState(false);

  const setStepCompletedAtIndex = (index: number, completed: boolean) => {
    setStepCompleted((prev) => {
      const newStepCompleted = [...prev];
      newStepCompleted[index] = completed;
      return newStepCompleted;
    });
  };

  const steps: StepType[] = [
    {
      label: "Cadastro de\nUsuário",
      completed: false,
      validate: () => {
        const { nome, email, cpf, senha } = userData;
        return (
          !!nome.trim() && !!email.trim() && !!cpf.trim() && !!senha.trim()
        );
      },
      icon: <Person />,
    },
    {
      label: "Dados\nPessoais",
      completed: false,
      validate: () => {
        const { identidade_genero, data_nascimento, escolaridade, profissao } =
          personalData;
        return (
          !!identidade_genero?.trim() &&
          !!data_nascimento &&
          !!escolaridade?.trim() &&
          !!profissao?.trim()
        );
      },
      icon: <Create />,
    },
    {
      label: "Dados de\nContato",
      completed: false,
      validate: () => !!contactData.telefone_celular?.trim(),
      icon: <Mail />,
    },
    {
      label: "Dados de\nEndereço",
      completed: false,
      validate: () => {
        const { cep, logradouro, numero, bairro, cidade, uf } = addressData;
        return (
          !!cep.trim() &&
          !!logradouro.trim() &&
          !!numero.trim() &&
          !!bairro.trim() &&
          !!cidade.trim() &&
          !!uf.trim()
        );
      },
      icon: <LocationOn />,
    },
    {
      label: "Dados\nProfissionais",
      completed: false,
      validate: () => professionalData.ambito_id !== "",
      icon: <Work />,
    },
  ];

  const handleNextStep = async () => {
    if (
      steps[activeStepIndex].validate() &&
      activeStepIndex + 1 < steps.length
    ) {
      setStepCompletedAtIndex(activeStepIndex, true);
      setActiveStepIndex(activeStepIndex + 1);
    } else if (activeStepIndex + 1 >= steps.length) {

      setLoadingRegister(true);

      await handleSendRegister();

      setLoadingRegister(false);
    }
  };

  const handlePrevStep = () => {
    if (activeStepIndex > 0) {
      setActiveStepIndex(activeStepIndex - 1);
    } else {
      if (onBackStart) {
        onBackStart();
      } else {
        navigate(`/login`);
      }
    }
  };

  const handleSendRegister = async () => {

    try {
      // Separar os dados de deficiência do personalData corretamente
      const deficiencyData = personalData.possui_deficiencia
        ? { deficiencia: disabilities }
        : null;

      const { 
        //possui_deficiencia, 
        //nome_social, 
        ...filteredPersonalData 
      } = personalData;

      // Criar o payload no formato esperado
      const payload = {
        userData: {
          nome: userData.nome,
          email: userData.email,
          provedor: userData.provedor,
          tipo_usuario: userData.tipo,
          CPF: userData.cpf,
          senha: userData.senha,
        },
        personalData: {
          ...filteredPersonalData,
          data_nascimento: formatarDatetimeToDataDb(filteredPersonalData.data_nascimento),
        },
        addressData,
        contactData,
        professionalData: {
          ambito_id: professionalData.ambito_id || "",
          municipio_id: professionalData.municipio_id || "",
          lotacao_id: professionalData.lotacao_id || "",
          vinculo_id: professionalData.vinculo_id || "",
          funcao_id: professionalData.funcao_id || "",
          superintendencia_id: professionalData.superintendencia_id || "",
          unidade_id: professionalData.unidade_id || "",
          diretoria_regional_id: professionalData.diretoria_regional_id || "",
          diretoria_interna_id: professionalData.diretoria_interna_id || "",
          instituicao_id: professionalData.instituicao_id || "",
          orgao_atuacao_id: professionalData.orgao_atuacao_id || "",
          areas_atuacao: areasActivity,
        },
        deficiencyData,
        captcha_token: captchaToken || undefined,
      };

      if (onRegisterSubmit) {
        await onRegisterSubmit(payload);
        onRegisterSuccess?.();
        return;
      }

      const API_URL = process.env.REACT_APP_API_URL || "http://200.198.9.108/api";

      const response = await axios.post(
        `${API_URL}/cadastronovoparticipante`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        },
      );

      if (response.status >= 200 && response.status < 300) {
        setStepCompletedAtIndex(steps.length - 1, true);
        setRegistrationCompleted(true);
      } else {
        alert("Houve um erro ao enviar os dados. Por favor, tente novamente.");
      }
    } catch (error) {
      console.error("Erro ao enviar os dados:", error);
      alert("Houve um erro ao enviar os dados. Por favor, tente novamente.");
    }
  };

  const handleUpdateUser = async (step: number) => {
    /* steps 1 usuário, 2 dados pessoais, 3 dados de contato, 4 dados endereco e 5 dados profissionais */

      setLoadingUpdate(true);

      if (!userData.id) {
        alert('Erro ao alterar informações, por favor tente novamente');
        setLoadingUpdate(false);
        return;
      }

      // Separar os dados de deficiência do personalData corretamente
      //const deficiencyData = personalData.possui_deficiencia ? { disabilities } : null;

      const { 
        //possui_deficiencia, 
        //nome_social, 
        ...filteredPersonalData 
      } = personalData;

      // Criar o payload no formato esperado
      const payload = {
        step: step,
        userData: {
          nome: userData.nome,
          email: userData.email,
          provedor: userData.provedor,
          cpf: userData.cpf,
          senha: userData.senha,
          tipo: userData.tipo,
          force_password_change: userData.force_password_change === true,
          password: userData.senha ? userData.senha : undefined,
          password_confirmation: userData.confirmarSenha ? userData.confirmarSenha : undefined,
        },
        personalData: {
          ...filteredPersonalData,
          data_nascimento: formatarDatetimeToDataDb(filteredPersonalData.data_nascimento),
          disabilities: disabilities,
        },
        addressData,
        contactData,
        professionalData: {
          ambito_id: professionalData.ambito_id || "",
          municipio_id: professionalData.municipio_id || "",
          lotacao_id: professionalData.lotacao_id || "",
          vinculo_id: professionalData.vinculo_id || "",
          funcao_id: professionalData.funcao_id || "",
          superintendencia_id: professionalData.superintendencia_id || "",
          unidade_id: professionalData.unidade_id || "",
          diretoria_regional_id: professionalData.diretoria_regional_id || "",
          diretoria_interna_id: professionalData.diretoria_interna_id || "",
          instituicao_id: professionalData.instituicao_id || "",
          orgao_atuacao_id: professionalData.orgao_atuacao_id || "",
          areas_atuacao: areasActivity,
        },
      };

      const result = await updateUserComplete(userData.id, payload);

      alert(result);
      window.dispatchEvent(new CustomEvent(CADASTRO_USER_UPDATED_EVENT));

      setLoadingUpdate(false);
    
  };

  const validadeExistsUser = async () => {
    setLoadingValidateExistsUser(true);

    try {
      let dataValidate : ValidadeUser = {
        cpf: userData.cpf,
        email: userData.email
      };

      if (userData.id) {
        dataValidate = {
          ...dataValidate,
          id: userData.id,
        };
      }

      const result = await validateExistsUsers(dataValidate);

      if (result.exists) {
        const messages = result.errors ? result.errors.join(' | ') : '';

        return {
          exists: result.exists,
          message: messages 
        };
      }

      return {
        exists: false,
      };
    } finally {
      setLoadingValidateExistsUser(false);
    }
  };

  const handleNavigate = (userType: string) => {
    navigate("/cadastro", { state: { userType } });
  };

  const value: CadastroContextType = {
    loadingRegister,
    setLoadingRegister,
    loadingUpdate,
    setLoadingUpdate,
    loadingValidateExistsUser,
    userData,
    setUserData,
    personalData,
    setPersonalData,
    addressData,
    setAddressData,
    contactData,
    setContactData,
    professionalData,
    setProfessionalData,
    disabilities,
    setDisabilities,
    areasActivity, 
    setAreasActivity,
    steps,
    activeStepIndex,
    setActiveStepIndex,
    stepCompleted,
    setStepCompletedAtIndex,
    captchaToken,
    setCaptchaToken,
    registrationCompleted,
    setRegistrationCompleted,
    handleNextStep,
    handlePrevStep,
    handleNavigate,
    handleUpdateUser,
    validadeExistsUser,
  };

  return (
    <CadastroContext.Provider value={value}>
      {children}
    </CadastroContext.Provider>
  );
};

export const useCadastro = () => {
  const context = useContext(CadastroContext);
  if (!context) {
    throw new Error("useCadastro deve ser usado dentro de um CadastroProvider");
  }
  return context;
};
